<script setup lang="ts">
import { getCategoryIconPath } from "~/utils/category-icons";
import { getCategorySearchPath, toTitleCase } from "~/utils/search-catalog";
import type {
  SearchCategoryOption,
  SearchServiceOption,
} from "~/utils/search-catalog";

const props = defineProps<{
  categories: SearchCategoryOption[];
  services: SearchServiceOption[];
}>();

const localeRoute = useLocaleRoute();
const { t } = useI18n();

function sortCatalog<T extends { sortOrder?: number | null; name: string }>(
  items: T[],
) {
  return [...items].sort(
    (a, b) =>
      Number(a.sortOrder == null) - Number(b.sortOrder == null) ||
      (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
      a.name.localeCompare(b.name),
  );
}

function categoryLabel(category: string) {
  const shortKey = `category_short.${category}`;
  const shortTranslated = t(shortKey);

  if (shortTranslated !== shortKey) return shortTranslated;

  const key = `category.${category}`;
  const translated = t(key);

  return translated === key ? toTitleCase(category) : translated;
}

const popularCategories = computed(() =>
  sortCatalog(props.categories)
    .slice(0, 8)
    .map(category => ({
      title: categoryLabel(category.name),
      category: category.name,
      icon: getCategoryIconPath(category.name),
    })),
);

function getSearchRoute(category?: string) {
  if (!category) return localeRoute("/workers");

  return localeRoute(getCategorySearchPath(category));
}
</script>

<template>
  <section class="w-full bg-white py-10 md:py-14">
    <div class="mx-auto w-full max-w-[1280px] px-4 md:px-6">
      <div class="mb-6 flex items-end justify-between gap-4 md:mb-8">
        <h2
          class="font-gabarito text-2xl font-bold leading-none text-app-text md:text-3xl"
        >
          {{ $t("popular_services.title") }}
        </h2>

        <NuxtLink
          :to="getSearchRoute()"
          class="hidden pb-0.5 text-sm font-semibold text-primary transition-colors duration-300 hover:text-primary-400 sm:inline-flex"
        >
          {{ $t("popular_services.actions.more") }}
        </NuxtLink>
      </div>

      <div class="grid grid-cols-2 gap-y-4 sm:grid-cols-4 lg:grid-cols-8">
        <NuxtLink
          v-for="item in popularCategories"
          :key="item.category"
          :to="getSearchRoute(item.category)"
          class="group flex min-h-[138px] flex-col items-center px-2 pb-3 pt-2 text-center sm:min-h-[146px]"
        >
          <span
            class="flex h-[68px] w-[82px] shrink-0 items-center justify-center sm:h-[72px] sm:w-[86px] lg:h-[76px] lg:w-[90px]"
          >
            <img
              v-if="item.icon"
              :src="item.icon"
              alt=""
              class="h-full w-full object-contain transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:scale-[1.025]"
              loading="lazy"
              decoding="async"
              aria-hidden="true"
            />
          </span>

          <span
            class="mt-2 flex min-h-[40px] max-w-[150px] items-start justify-center text-[15px] font-semibold leading-5 text-app-text transition-colors duration-300 group-hover:text-primary"
          >
            {{ item.title }}
          </span>
        </NuxtLink>
      </div>

      <NuxtLink
        :to="getSearchRoute()"
        class="mt-4 flex h-11 items-center justify-center text-sm font-semibold text-primary sm:hidden"
      >
        {{ $t("popular_services.actions.more") }}
      </NuxtLink>
    </div>
  </section>
</template>
